import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { useParams, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import _ from 'lodash';

import {
    GoBackButton,
    Text,
    ContainedButton,
    OverlapCard,
    StyledDialog,
    DeleteIcon2,
    OutlinedButton,
    ConfirmDialog,
} from '../../components/index';

import CreateSessionCard from '../../components/Provider/Appointment/CreateSessionCard';
import ProviderCard from '../../components/Provider/Appointment/ProviderCard';
import PatientCard from '../../components/Provider/Appointment/PatientCard';
import TotalCreditsCard from '../../components/Provider/Appointment/TotalCreditsCard';
import RulesCard from '../../components/Provider/Appointment/RulesCard';

import { providerPageWidth, GROUP_RULES_TH } from '../../constant';

import {
    createGroup,
    editGroup,
    deleteGroup,
    canStart,
    GET_CALENDAR,
    GET_MEET,
} from '../../services/provider';
import { alertError, alertSuccess } from '../../redux/stateActions';

dayjs.extend(objectSupport);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

const creditGroupCalculate = (startTime, endTime, numberOfParticipants) => {
    /*
        group session
        first participant, first 30 minutes cost 100 credits
        second and third participants, first 30 minutes cost 90 credits for each participant
        fourth, fifth, sixth and so on, first 30 minutes cost 80 credits for each participant
        after 30 minutes cost 2 credit per minute for each participant
    */
    const duration = endTime.diff(startTime, 'minute');

    if (duration < 0) return 0;

    let cost = 0;
    const exceedDuration = duration - 30;

    // first participant in first 30 minutes
    if (numberOfParticipants > 0) {
        cost += 100;
    }

    // second and third participant in first 30 minutes
    if (numberOfParticipants > 1) {
        cost += 90;
    }
    if (numberOfParticipants > 2) {
        cost += 90;
    }

    // the rest of participants in first 30 minutes
    if (numberOfParticipants > 3) {
        cost += (numberOfParticipants - 3) * 80;
    }

    // after 30 minutes
    if (exceedDuration > 0) {
        cost += exceedDuration * 2 * numberOfParticipants;
    }

    return cost;
};

const CreateGroup = ({ history, location }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const { id } = useParams();
    const matchEdit = useRouteMatch('/provider/calendar/group/edit/:id');

    useEffect(() => {
        const { search } = location;
        const queryParams = new URLSearchParams(search);
        const date = queryParams.get('date');
        const staff = queryParams.get('staff');

        if (date && dayjs.isDayjs(dayjs(date))) {
            setAppointmentDate(dayjs(date));
        }
        if (staff) {
            setSelectedStaff(staff);
        } else if (
            !_.isEmpty(user) &&
            !user.staff.deactivated &&
            user.staff.staff_role.name !== 'Admin'
        ) {
            setSelectedStaff(user.staff.id);
        }
    }, []);

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [appointmentDate, setAppointmentDate] = useState(null);
    const [appointmentStartTime, setAppointmentStartTime] = useState('');
    const [appointmentEndTime, setAppointmentEndTime] = useState('');
    const [selectedPatients, setSelectedPatients] = useState([]);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [credits, setCredits] = useState(0);
    const [selectedRules, setSelectedRules] = useState([]);
    const [calendar, setCalerdar] = useState({});
    const [creditsError, setCreditsError] = useState(false);
    const [overlapData, setOverlapData] = useState({});
    const [selectedStaff, setSelectedStaff] = useState('');

    const disabledEditMeet = useMemo(() => {
        return selectedPatients.some((p) => p.disabled);
    }, [selectedPatients]);

    const handleGoBack = () => {
        history.goBack();
    };

    /*--------------- Get Calendar ---------------*/
    const {
        data: getCalendarData,
        error: getCalendarError,
        loading: getCalendarLoading,
    } = useQuery(GET_CALENDAR, {
        variables: {
            meet: matchEdit ? id : undefined,
            staff: selectedStaff,
            date: !_.isNull(appointmentDate) && dayjs(appointmentDate).format('YYYY-MM-DD'),
        },
        skip: _.isEmpty(user) || _.isNull(appointmentDate) || !selectedStaff,
    });

    useEffect(() => {
        if (getCalendarError) {
            dispatch(alertError(`Cannot get group and 1on1 session: ${getCalendarError.message}`));
        }

        if (getCalendarData && getCalendarData.calendars.length > 1) {
            dispatch(alertError(`More than one calendar found`));
            return;
        }

        if (getCalendarData) {
            if (_.isEmpty(getCalendarData.calendars)) {
                setCalerdar({});
            } else {
                setCalerdar(getCalendarData.calendars[0]);
            }
        }
    }, [getCalendarData, getCalendarError]);

    useEffect(() => {
        const newSelectedRules = GROUP_RULES_TH.map((rule) => ({ ...rule, checked: false }));
        setSelectedRules(newSelectedRules);
    }, []);

    /*--------------- Get Meet for duplicate only ---------------*/
    const {
        data: getMeetData,
        error: getMeetError,
        loading: getMeetLoading,
    } = useQuery(GET_MEET, {
        variables: { id: id },
        skip: !id,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getMeetError) {
            dispatch(alertError(`Cannot get Meet data: ${getMeetError.message}`));
        }

        if (getMeetData && _.isEmpty(getMeetData.meet)) {
            dispatch(alertError(`Meet not found`));
            handleGoBack();
        }

        if (getMeetData && !_.isEmpty(getMeetData.meet)) {
            const {
                type,
                name,
                description,
                startTime,
                endTime,
                staff,
                sessions,
                rules,
                startDate,
            } = getMeetData.meet;

            if (type !== 'group') {
                handleGoBack();
                return;
            }

            const endDateTime = dayjs(startDate).set({
                hour: endTime.split(':')[0],
                minute: endTime.split(':')[1],
            });

            if (matchEdit && dayjs().isAfter(endDateTime, 'minute')) {
                handleGoBack();
                return;
            }

            if (sessions.length === 0) return;
            if (sessions.length > 1) {
                dispatch(alertError(`More than one session found`));
                return;
            }

            setName(name);
            setDescription(description);
            setSelectedStaff(staff.id);

            if (matchEdit) {
                setAppointmentDate(dayjs(startDate));
            }

            // set rules
            const fetchRules = Array.from(rules);
            const newSelectedRules = GROUP_RULES_TH.map((r) =>
                fetchRules.includes(r.id) ? { ...r, checked: true } : { ...r, checked: false }
            );
            setSelectedRules(newSelectedRules);

            // set start time
            setAppointmentStartTime(
                dayjs()
                    .set({
                        hour: startTime.split(':')[0],
                        minute: startTime.split(':')[1],
                    })
                    .format('HH:mm')
            );

            // set end time
            setAppointmentEndTime(
                dayjs()
                    .set({
                        hour: endTime.split(':')[0],
                        minute: endTime.split(':')[1],
                    })
                    .format('HH:mm')
            );

            // set patient
            const newSelectedPatients = [];
            sessions[0].patient_summaries
                .filter((item) => item?.patient?.user)
                .forEach((summary) => {
                    newSelectedPatients.push({
                        id: summary.patient.id,
                        HNCode: summary.patient.HNCode,
                        user: summary.patient.user,
                        disabled: matchEdit && summary?.checkinTime,
                        patientSummaryId: summary.id,
                    });
                });
            setSelectedPatients(newSelectedPatients);
        }
    }, [getMeetError, getMeetData]);

    useEffect(() => {
        if (
            appointmentStartTime !== '' &&
            appointmentEndTime !== '' &&
            !_.isEmpty(selectedPatients)
        ) {
            const startTime = dayjs().set({
                hour: appointmentStartTime.split(':')[0],
                minute: appointmentStartTime.split(':')[1],
            });
            const endTime = dayjs().set({
                hour: appointmentEndTime.split(':')[0],
                minute: appointmentEndTime.split(':')[1],
            });
            setCredits(creditGroupCalculate(startTime, endTime, selectedPatients.length));
        }
    }, [appointmentStartTime, appointmentEndTime, selectedPatients]);

    useEffect(() => {
        const checkCredits = async () => {
            try {
                const res = await canStart({ id: user.staff.hospital.id, amount: credits });

                if (res.status === 200) {
                    if (res.data.canStart === true) {
                        setCreditsError(false);
                    } else {
                        setCreditsError(true);
                    }
                } else {
                    throw new Error(res);
                }
            } catch (error) {
                dispatch(alertError(`Cannot check credits: ${error.message}`));
            }
        };

        if (credits > 0) {
            checkCredits();
        }
    }, [credits]);

    const handleSelectStaff = (id) => {
        setSelectedStaff(id);
        setErrors(errors.filter((error) => error.property !== 'selectedProvider'));
    };

    const handleAddPatient = (patientArr) => {
        setSelectedPatients(patientArr);
        setErrors(errors.filter((error) => error.property !== 'selectedPatients'));
    };

    const handleRemovePatient = (id) => {
        setSelectedPatients(selectedPatients.filter((patient) => patient.id !== id));
        setErrors(errors.filter((error) => error.property !== 'selectedPatients'));
    };

    const handleDateChange = (date) => {
        setAppointmentDate(date);
        setErrors(errors.filter((error) => error.property !== 'appointmentDate'));
    };

    const handleStartTimeChange = (time) => {
        setAppointmentStartTime(time);
        setErrors(errors.filter((error) => error.property !== 'appointmentStartTime'));
    };
    const handleEndTimeChabge = (time) => {
        setAppointmentEndTime(time);
        setErrors(errors.filter((error) => error.property !== 'appointmentEndTime'));
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
    };

    const handleDescriptionChange = (e) => {
        setDescription(e.target.value);
    };

    const handlePatientTimeChange = () => {
        return;
    };

    const handleSelectRule = (id) => {
        const newSelectedRules = [...selectedRules];

        const selectedRuleIndex = newSelectedRules.findIndex((r) => r.id === id);

        if (newSelectedRules[selectedRuleIndex].checked === true) {
            newSelectedRules[selectedRuleIndex].checked = false;
        } else {
            newSelectedRules[selectedRuleIndex].checked = true;
        }

        setSelectedRules(newSelectedRules);
    };

    const onCancelConfirm = () => {
        setOverlapData({});
    };

    const handleSubmit = async () => {
        setOverlapData({});

        const data = {
            id: id,
            hospital: user.staff.hospital.id,
            name: name.trim(),
            description: description.trim(),
            staff: selectedStaff,
            patients: selectedPatients.map((patient) => ({
                id: patient.id,
                patientSummaryId: patient?.patientSummaryId,
            })),
            date: dayjs(appointmentDate).format('YYYY-MM-DD'),
            startTime: dayjs()
                .set('date', dayjs(appointmentDate).get('date'))
                .set('hour', appointmentStartTime.split(':')[0])
                .set('minute', appointmentStartTime.split(':')[1])
                .format('HH:mm:ss.SSS'),
            endTime: dayjs()
                .set('date', dayjs(appointmentDate).get('date'))
                .set('hour', appointmentEndTime.split(':')[0])
                .set('minute', appointmentEndTime.split(':')[1])
                .format('HH:mm:ss.SSS'),
            rules: selectedRules.filter((r) => r.checked).map((r) => r.id),
        };

        try {
            setLoading(true);

            console.log('data', data);

            let res;

            if (matchEdit) {
                res = await editGroup(data);
            } else {
                res = await createGroup(data);
            }

            console.log('res', res);

            if (res?.status === 200) {
                dispatch(alertSuccess(`Group Session ${matchEdit ? 'Edited' : 'Created'}`));

                if (matchEdit) {
                    history.push(`/provider/session/group/${id}`);
                } else {
                    history.push(`/provider/calendar`);
                }
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setLoading(false);

            dispatch(
                alertError(
                    `Cannot ${matchEdit ? 'edit' : 'create'} Group Session: ${JSON.stringify(
                        error.response?.data?.message
                    )}`
                )
            );
        }
    };

    const handleSave = async () => {
        const newErrors = [];

        if (name === '') {
            newErrors.push({
                property: 'name',
                message: 'Please provide a valid Name.',
            });
        }

        if (description === '') {
            newErrors.push({
                property: 'description',
                message: 'Please provide a valid Description.',
            });
        }

        if (appointmentDate === null) {
            newErrors.push({
                property: 'appointmentDate',
                message: 'Please provide a valid Date.',
            });
        }

        if (appointmentStartTime === '') {
            newErrors.push({
                property: 'appointmentStartTime',
                message: 'Please provide a valid Start Time.',
            });
        }

        if (appointmentEndTime === '') {
            newErrors.push({
                property: 'appointmentEndTime',
                message: 'Please provide a valid End Time.',
            });
        }

        if (selectedStaff === '') {
            newErrors.push({
                property: `selectedProvider`,
                message: 'Please select provider.',
            });
        }

        if (
            dayjs()
                .set({
                    hour: appointmentStartTime.split(':')[0],
                    minute: appointmentStartTime.split(':')[1],
                })
                .isSameOrAfter(
                    dayjs().set({
                        hour: appointmentEndTime.split(':')[0],
                        minute: appointmentEndTime.split(':')[1],
                    }),
                    'minute'
                )
        ) {
            newErrors.push({
                property: 'appointmentEndTime',
                message: 'End Time must be after Start Time.',
            });
        }

        if (
            appointmentDate !== null &&
            (dayjs(appointmentDate)
                .set({
                    hour: appointmentStartTime.split(':')[0],
                    minute: appointmentStartTime.split(':')[1],
                })
                .isSameOrBefore(dayjs()) ||
                dayjs(appointmentDate)
                    .set({
                        hour: appointmentEndTime.split(':')[0],
                        minute: appointmentEndTime.split(':')[1],
                    })
                    .isSameOrBefore(dayjs()))
        ) {
            newErrors.push({
                property: 'appointmentEndTime',
                message: 'Session must be in the future.',
            });
        }

        if (_.isEmpty(selectedPatients)) {
            newErrors.push({
                property: 'selectedPatients',
                message: 'Please provide a valid Client.',
            });
        } else if (selectedPatients.length > 6) {
            newErrors.push({
                property: 'selectedPatients',
                message: 'Number of participant exceeded.',
            });
        }

        setErrors(newErrors);
        if (!_.isEmpty(newErrors)) return;

        // check meet time overlap
        if (!_.isEmpty(calendar)) {
            const { meets } = calendar;
            const startTimeOverlap = meets.find((meet) =>
                dayjs()
                    .set({
                        hour: appointmentStartTime.split(':')[0],
                        minute: appointmentStartTime.split(':')[1],
                    })
                    .isBetween(
                        dayjs().set({
                            hour: meet.startTime.split(':')[0],
                            minute: meet.startTime.split(':')[1],
                        }),
                        dayjs().set({
                            hour: meet.endTime.split(':')[0],
                            minute: meet.endTime.split(':')[1],
                        }),
                        'minute',
                        '[]'
                    )
            );
            const endTimeOverlap = meets.find((meet) =>
                dayjs()
                    .set({
                        hour: appointmentEndTime.split(':')[0],
                        minute: appointmentEndTime.split(':')[1],
                    })
                    .isBetween(
                        dayjs().set({
                            hour: meet.startTime.split(':')[0],
                            minute: meet.startTime.split(':')[1],
                        }),
                        dayjs().set({
                            hour: meet.endTime.split(':')[0],
                            minute: meet.endTime.split(':')[1],
                        }),
                        'minute',
                        '[]'
                    )
            );
            if (startTimeOverlap) {
                setOverlapData(startTimeOverlap);
                return;
            }
            if (endTimeOverlap) {
                setOverlapData(endTimeOverlap);
                return;
            }
        }

        handleSubmit();
    };

    const [openDelete, setOpenDelete] = useState(false);

    const onDelete = () => {
        setOpenDelete(true);
    };

    const onCancelDelete = () => {
        setOpenDelete(false);
    };

    const onConfirmDelete = async () => {
        try {
            setLoading(true);

            let res = await deleteGroup(id);

            console.log('res', res);

            if (res?.status === 200) {
                dispatch(alertSuccess(`group session Deleted`));

                history.push(`/provider/calendar`);
            } else {
                throw new Error(res);
            }
        } catch (error) {
            console.log('error', error.response);
            setLoading(false);

            dispatch(
                alertError(
                    `Cannot delete group Session: ${JSON.stringify(error.response?.data?.message)}`
                )
            );
        }
    };

    return (
        <div
            style={{
                padding: '41px 20px 50px',
                maxWidth: providerPageWidth,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '20px',
            }}
        >
            <ConfirmDialog
                open={openDelete}
                title="Are you sure you want to delete this Session?"
                contentText="Please confirm if you want to proceed with the deletion."
                cancelText="Cancel"
                onCancel={onCancelDelete}
                confirmText="Yes, Delete"
                onConfirm={onConfirmDelete}
                loading={loading}
            />
            <StyledDialog
                scroll="body"
                open={!_.isEmpty(overlapData)}
                onClose={onCancelConfirm}
                aria-labelledby="warningDialog"
                aria-describedby="warning dialog"
                PaperProps={{ style: { borderRadius: '16px' } }}
            >
                <OverlapCard
                    data={overlapData}
                    onCancelConfirm={onCancelConfirm}
                    onConfirm={handleSubmit}
                />
            </StyledDialog>
            <GoBackButton $text onClick={handleGoBack} />
            <Text $fontSize="20px" $bold>
                {`${matchEdit ? 'Edit' : 'Create'} Group session`}
            </Text>
            <CreateSessionCard
                errors={errors}
                loading={loading || getCalendarLoading || getMeetLoading}
                disabledDate={disabledEditMeet}
                disabledTime={disabledEditMeet}
                appointmentDate={appointmentDate}
                handleDateChange={handleDateChange}
                appointmentStartTime={appointmentStartTime}
                handleStartTimeChange={handleStartTimeChange}
                appointmentEndTime={appointmentEndTime}
                handleEndTimeChabge={handleEndTimeChabge}
                name={name}
                handleNameChange={handleNameChange}
                description={description}
                handleDescriptionChange={handleDescriptionChange}
                selectedStaff={selectedStaff}
                setSelectedStaff={setSelectedStaff}
            />
            {user.staff.staff_role.name !== 'Provider' && (
                <ProviderCard
                    errors={errors}
                    loading={loading || getCalendarLoading || getMeetLoading}
                    selectedStaff={selectedStaff}
                    handleSelectStaff={handleSelectStaff}
                    disabled={disabledEditMeet}
                />
            )}
            <PatientCard
                layout="GROUP"
                errors={errors}
                loading={loading || getCalendarLoading || getMeetLoading}
                selectedPatients={selectedPatients}
                handleAddPatient={handleAddPatient}
                handlePatientTimeChange={handlePatientTimeChange}
                handleRemovePatient={handleRemovePatient}
            />
            <TotalCreditsCard data={credits} error={creditsError} />
            <RulesCard rules={selectedRules} onSelect={handleSelectRule} />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: matchEdit ? 'space-between' : 'flex-end',
                }}
            >
                {matchEdit && (
                    <OutlinedButton
                        $color="pink"
                        $width="99px"
                        $borderWidth="1.5px"
                        $padding="0"
                        onClick={onDelete}
                        disabled={disabledEditMeet}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: '3px',
                            }}
                        >
                            <DeleteIcon2
                                width="20px"
                                height="20px"
                                fill={disabledEditMeet ? 'gray4' : 'pink'}
                            />
                            <Text $color={disabledEditMeet ? 'gray4' : 'pink'}>Delete</Text>
                        </div>
                    </OutlinedButton>
                )}
                <ContainedButton
                    style={{ height: '32px', width: '93px' }}
                    onClick={handleSave}
                    disabled={
                        name === '' ||
                        description === '' ||
                        _.isEmpty(selectedPatients) ||
                        (user.staff.staff_role.name === 'Provider' && user.staff.deactivated)
                    }
                    loading={loading || getCalendarLoading || getMeetLoading}
                >
                    Next
                </ContainedButton>
            </div>
        </div>
    );
};

CreateGroup.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};

export default CreateGroup;
